/**
 * What an event costs, and what its button should therefore say.
 *
 * This lived in four places — EventListItem, ScrollEventCard, hand/ListCard and
 * FeaturedStrip — and the fourth copy got it wrong: it read only the cents, so
 * every ingested listing (whose price is off-platform, leaving the cents at 0)
 * rendered as "JOIN · FREE" on the featured strip while the identical event in
 * the grid below correctly said TICKETS. Same payload, two answers.
 *
 * So the rule lives here once. Callers get the facts and format their own label,
 * because the strip puts the price on the button and the list row deliberately
 * doesn't — that part is design, not logic.
 */

/**
 * @param {object} event a card payload from MapEventCard (featured wall), or any
 *   shape carrying the same fields.
 * @param {{decimals?: number}} [opts] how many decimal places the amount gets.
 *   The wide table row shows "$169.00" because it has the space; the compact
 *   cards round to "$169". That difference is deliberate, so it's a parameter
 *   rather than something for each caller to reimplement.
 * @returns {{
 *   shownPriceCents: number,
 *   isFree: boolean,
 *   isExternalTicketing: boolean,
 *   externalPrice: string,
 *   stillFunding: boolean,
 *   amount: string|null,
 *   priceText: string,
 * }}
 */
/**
 * Is a scraped display-price string actually a price?
 *
 * Ingestion fills `externalTicketDisplayPrice` with whatever sat in the listing's price slot,
 * which on some sites is a contact line — "EMAIL: INFO@HAMBLETONGALLERIES.COM" landed on the
 * wall as a $18-size headline and pushed TICKETS off the card. A price has a figure or a word
 * for no figure (free, donation, PWYC), is short, and is not an address of any kind.
 */
export function looksLikePrice(s) {
  const t = (s || '').trim();
  if (!t || t.length > 40) return false;
  if (/[@]|https?:|www\.|\.(com|ca|org|net|app)\b/i.test(t)) return false;
  return /\d|free|donation|pwyc|pay what|by donation|tbd|tba|sold out/i.test(t);
}

export function eventPricing(event, { decimals = 0 } = {}) {
  const {
    ticketPriceCents = 0,
    displayPriceCents,
    isExternalTicketing = false,
    externalTicketDisplayPrice,
    fundingType,
    status,
  } = event || {};

  // A tiered event costs whatever tier is on sale; ticketPriceCents is only the
  // base figure, and quoting it made cards disagree with the apply button.
  const shownPriceCents = displayPriceCents ?? ticketPriceCents;

  // The crux: an external listing is never "free". Its tickets are sold off
  // platform, so we hold no cents for it — 0 here means "we don't handle the
  // money", not "it costs nothing".
  const isFree = !isExternalTicketing && (!shownPriceCents || shownPriceCents <= 0);

  // Whatever price string the source published, when it published one — if it IS a price.
  const externalPrice = looksLikePrice(externalTicketDisplayPrice) ? externalTicketDisplayPrice.trim() : '';

  const stillFunding = fundingType === 'party_starter'
    && (status === 'funding' || status === 'processing_greenlight');

  const amount = isFree || shownPriceCents <= 0 ? null : `$${(shownPriceCents / 100).toFixed(decimals)}`;

  // Empty for an external listing with no published price: the CTA already says
  // "Tickets", and printing the word twice reads as a bug.
  const priceText = isExternalTicketing ? externalPrice : (isFree ? 'Free' : (amount || ''));

  return { shownPriceCents, isFree, isExternalTicketing, externalPrice, stillFunding, amount, priceText };
}

export default eventPricing;
